<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="api-vod">
        <h2>Video Content</h2>
        <!-- Add search input -->
        <div class="search-container">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Search videos..." 
            class="search-input"
            @input="handleSearch"
          >
        </div>
        <div v-if="loading">Loading...</div>
        <div v-else-if="error">{{ error }}</div>
        <div v-else>
          <div v-if="filteredVideos.length">
            <h3>Videos:</h3>
            <ul>
              <li 
                v-for="video in filteredVideos" 
                :key="video.videoId"
                class="video-card"
                :class="{ 'expanded': expandedVideos.includes(video.videoId) }"
                @click="toggleVideo(video.videoId)"
              >
                <div class="video-header">
                  <div class="video-title">{{ video.title }}</div>
                  <div class="expand-icon">{{ expandedVideos.includes(video.videoId) ? '▼' : '▶' }}</div>
                </div>
                <transition name="expand">
                  <div v-show="expandedVideos.includes(video.videoId)" class="video-details">
                    <!-- Add video player -->
                    <div class="video-player-container">
                      <iframe
                        v-if="expandedVideos.includes(video.videoId)"
                        :key="`player-${video.videoId}-${expandedVideos.includes(video.videoId)}`"
                        :src="`https://embed.api.video/vod/${video.videoId}`"
                        width="100%"
                        height="400"
                        frameborder="0"
                        scrolling="no"
                        allowfullscreen
                        class="video-player"
                      ></iframe>
                    </div>
                    <div class="video-info">
                      ID: {{ video.videoId }}<br />
                      Title: {{ video.title }}<br />
                      Description: {{ video.description }}<br />
                      Thumbnail: {{ video.thumbnailUrl }}<br />
                      Duration: {{ video.duration }}<br />
                      Created At: {{ video.createdAt }}<br />
                      Updated At: {{ video.updatedAt }}<br />
                      Status: {{ video.status }}<br />
                      Language: {{ video.language }}<br />
                      <!-- Add encoding status information -->
                      <div v-if="video.encodingStatus">
                        <strong>Encoding Status:</strong>
                        <span class="status-badge" :class="{
                          'status-playable': video.encodingStatus.encoding?.playable,
                          'status-processing': !video.encodingStatus.encoding?.playable
                        }">
                          {{ video.encodingStatus.encoding?.playable ? 'Ready' : 'Processing' }}
                        </span>
                        <div v-if="video.encodingStatus.encoding">
                          <strong>Qualities:</strong>
                          <ul class="qualities-list">
                            <li v-for="quality in video.encodingStatus.encoding.qualities" :key="quality.quality">
                              {{ quality.type }} - {{ quality.quality }} ({{ quality.status }})
                            </li>
                          </ul>
                          <strong>Metadata:</strong><br />
                          Resolution: {{ video.encodingStatus.encoding.metadata.width }}x{{ video.encodingStatus.encoding.metadata.height }}<br />
                          Duration: {{ video.encodingStatus.encoding.metadata.duration }}s<br />
                          Framerate: {{ video.encodingStatus.encoding.metadata.framerate }}fps<br />
                        </div>
                      </div>
                    </div>
                  </div>
                </transition>
              </li>
            </ul>
            
            <!-- Pagination Controls -->
            <div class="pagination">
              <button 
                :disabled="currentPage === 1" 
                @click="changePage(currentPage - 1)"
                class="pagination-btn"
              >
                Previous
              </button>
              <span class="page-info">Page {{ currentPage }} of {{ totalPages }}</span>
              <button 
                :disabled="currentPage === totalPages" 
                @click="changePage(currentPage + 1)"
                class="pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
          <div v-else>No videos found</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'APIVOD',
  components: {
    Sidebar
  },
  data() {
    return {
      accessToken: null,
      videos: [],
      loading: true,
      error: null,
      currentPage: 1,
      pageSize: 25,
      totalVideos: 0,
      totalPages: 1,
      searchQuery: '',
      expandedVideos: [],
      searchTimeout: null, // Add debounce timeout
    };
  },
  computed: {
    filteredVideos() {
      return this.videos;
    }
  },
  methods: {
    toggleVideo(videoId) {
      const index = this.expandedVideos.indexOf(videoId);
      if (index === -1) {
        // Close any other open videos before opening a new one
        this.expandedVideos = [videoId];
      } else {
        this.expandedVideos = [];
      }
    },
    handleSearch() {
      // Debounce the search to avoid too many API calls
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.fetchVideos(1);
      }, 300);
    },
    async fetchVideoStatus(videoId) {
      try {
        const statusResponse = await axios.get(`https://ws.api.video/videos/${videoId}/status`, {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          }
        });
        return statusResponse.data;
      } catch (err) {
        console.error(`Error fetching status for video ${videoId}:`, err);
        return null;
      }
    },
    async fetchVideos(page = 1) {
      try {
        this.loading = true;
        const params = {
          currentPage: page,
          pageSize: this.pageSize
        };

        // Add title parameter only if search query exists
        if (this.searchQuery) {
          params.title = this.searchQuery;
        }

        const videosResponse = await axios.get('https://ws.api.video/videos', {
          headers: {
            'Authorization': `Bearer ${this.accessToken}`
          },
          params
        });

        // Fetch status for each video
        const videosWithStatus = await Promise.all(
          videosResponse.data.data.map(async (video) => {
            const status = await this.fetchVideoStatus(video.videoId);
            return {
              ...video,
              encodingStatus: status
            };
          })
        );

        this.videos = videosWithStatus;
        this.totalVideos = videosResponse.data.pagination.itemsTotal;
        this.totalPages = videosResponse.data.pagination.pagesTotal;
        this.currentPage = videosResponse.data.pagination.currentPage;
        this.loading = false;
      } catch (err) {
        this.error = `Error: ${err.message}`;
        this.loading = false;
      }
    },
    async changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        await this.fetchVideos(newPage);
      }
    }
  },
  async created() {
    try {
      // First, get the access token
      const authResponse = await axios.post('https://ws.api.video/auth/api-key', {
        apiKey: 'LXcWKZfW4Kn45Y9Z3v5IdNYl1btN228g0sSVSG2hAOL'
      }, {
        headers: {
          'accept': 'application/json',
          'content-type': 'application/json'
        }
      });

      this.accessToken = authResponse.data.access_token;

      // Then fetch the first page of videos
      await this.fetchVideos(1);
    } catch (err) {
      this.error = `Error: ${err.message}`;
      this.loading = false;
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  min-height: 100vh;
  background-color: #f8f9fa;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  padding: 2rem;
  background-color: white;
  min-height: 100vh;
}

.api-vod {
  max-width: 1200px;
  margin: 0 auto;
}

.search-container {
  margin-bottom: 2rem;
}

.search-input {
  width: 100%;
  max-width: 500px;
  padding: 12px 20px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
}

.search-input:focus {
  outline: none;
  border-color: #4a90e2;
  box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.video-card {
  background: white;
  border-radius: 12px;
  margin: 1rem 0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.video-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.video-header {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 12px 12px 0 0;
}

.video-title {
  font-weight: 600;
  font-size: 1.2rem;
  color: #2d3436;
}

.expand-icon {
  font-size: 1.2rem;
  color: #4a90e2;
}

.video-details {
  padding: 1.5rem;
  background-color: #f8f9fa;
}

.video-info {
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
}

.qualities-list li {
  background-color: white;
  padding: 8px 12px;
  margin: 4px 0;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
  font-size: 0.9em;
  display: flex;
  justify-content: space-between;
}

.pagination {
  margin: 2rem 0;
  gap: 1rem;
}

.pagination-btn {
  padding: 10px 20px;
  border-radius: 8px;
  background-color: #4a90e2;
  transition: all 0.2s ease;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #357abd;
  transform: translateY(-1px);
}

.page-info {
  font-weight: 500;
  color: #4a90e2;
}

/* Add status badges */
.status-badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.8em;
  font-weight: 500;
  text-transform: uppercase;
}

.status-playable {
  background-color: #e3fcef;
  color: #006644;
}

.status-processing {
  background-color: #fff8e5;
  color: #8a6d00;
}

/* Grid layout for video details */
.video-details-grid {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  margin-top: 1.5rem;
}

@media (max-width: 1024px) {
  .video-details-grid {
    grid-template-columns: 1fr;
  }
  
  .main-content {
    margin-left: 0;
    padding: 1.5rem;
  }
}

@media (max-width: 768px) {
  .video-player-container {
    margin: 0 -1.5rem;
    border-radius: 0;
  }
  
  .video-header {
    padding: 1rem;
  }
}

/* Improved typography */
h2 {
  font-size: 2rem;
  margin-bottom: 2rem;
  color: #2d3436;
  font-weight: 700;
}

h3 {
  font-size: 1.5rem;
  margin: 2rem 0 1.5rem;
  color: #4a90e2;
}
</style> 